"use client";

import { useCallback } from "react";
import { authClient } from "@/lib/auth";
import { organizationsApi } from "@/lib/api";

export interface AuthSession {
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
  };
  session?: {
    activeOrganizationId?: string | null;
  };
}

export function useAuth() {
  const { data: session, isPending, error } = authClient.useSession();

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await authClient.signIn.email({ email, password });
    if (result.error) {
      throw new Error(result.error.message || "Sign in failed");
    }
    return result.data;
  }, []);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      name: string,
      options?: { orgName?: string; skipPersonalOrg?: boolean }
    ) => {
      const result = await authClient.signUp.email({ email, password, name });
      if (result.error) {
        throw new Error(result.error.message || "Sign up failed");
      }

      // Personal-org bootstrap is opt-out via `skipPersonalOrg` — callers in an
      // invite-accept context (see AcceptInvite) must skip this: creating a
      // personal org here and setting it active would clobber the org the
      // invited membership belongs to (the invitation-accept flow sets its
      // own active org explicitly).
      if (!options?.skipPersonalOrg && options?.orgName?.trim()) {
        try {
          const org = await organizationsApi.createPersonal(options.orgName.trim());
          await authClient.organization.setActive({ organizationId: org.id });
        } catch {
          // Non-fatal: user can create an org later from settings
        }
      }

      return result.data;
    },
    []
  );

  const signInWithGoogle = useCallback(async () => {
    const result = await authClient.signIn.social({
      provider: "google",
      callbackURL: "/strategies",
    });
    if (result.error) {
      throw new Error(result.error.message || "Google sign in failed");
    }
    return result.data;
  }, []);

  const signOut = useCallback(async () => {
    await authClient.signOut();
  }, []);

  const setActiveOrganization = useCallback(async (orgId: string) => {
    await authClient.organization.setActive({ organizationId: orgId });
  }, []);

  const activeOrganizationId =
    (session as AuthSession | null)?.session?.activeOrganizationId ?? null;

  return {
    session: session as AuthSession | null,
    isLoading: isPending,
    isAuthenticated: !!session?.user,
    activeOrganizationId,
    error,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    setActiveOrganization,
  };
}
